import { enableMapSet, produce } from "immer";
import { CanvasConfig } from "types";
import create from "zustand";

// Every key here (appId) maps to at most one entry, so Map/Set give O(1) membership checks and
// native .delete() instead of the no-dynamic-delete workaround plain objects needed.
enableMapSet();

interface EmbeddedAppState {
    // appId -> context passed at open time
    openCanvases: Map<string, Record<string, unknown>>;
    brokenApps: Set<string>;
    // appId -> active dynamic canvas config (key absent = hidden)
    dynamicCanvases: Map<string, CanvasConfig>;
}

export interface EmbeddedAppActions {
    openCanvas: (appId: string, context?: Record<string, unknown>) => void;
    closeCanvas: (appId: string) => void;
    markBroken: (appId: string) => void;
    resetBroken: (appId: string) => void;
    showDynamicCanvas: (appId: string, config: CanvasConfig) => void;
    hideDynamicCanvas: (appId: string) => void;
}

interface EmbeddedAppStore extends EmbeddedAppState {
    actions: EmbeddedAppActions;
}

export const useEmbeddedAppStore = create<EmbeddedAppStore>((set) => ({
    brokenApps: new Set(),
    openCanvases: new Map(),
    dynamicCanvases: new Map(),

    actions: {
        openCanvas: (appId, context = {}) =>
            set(
                produce((draft: EmbeddedAppStore) => {
                    draft.openCanvases.set(appId, context);
                }),
            ),

        closeCanvas: (appId) =>
            set(
                produce((draft: EmbeddedAppStore) => {
                    draft.openCanvases.delete(appId);
                }),
            ),

        markBroken: (appId) =>
            set(
                produce((draft: EmbeddedAppStore) => {
                    draft.brokenApps.add(appId);
                }),
            ),

        resetBroken: (appId) =>
            set(
                produce((draft: EmbeddedAppStore) => {
                    draft.brokenApps.delete(appId);
                }),
            ),

        showDynamicCanvas: (appId, config) =>
            set(
                produce((draft: EmbeddedAppStore) => {
                    draft.dynamicCanvases.set(appId, config);
                }),
            ),

        hideDynamicCanvas: (appId) =>
            set(
                produce((draft: EmbeddedAppStore) => {
                    draft.dynamicCanvases.delete(appId);
                }),
            ),
    },
}));

// Shared selectors so the same three facts aren't re-derived at each call site.
export const useIsAppBroken = (appId: string) => useEmbeddedAppStore((state) => state.brokenApps.has(appId));

export const useIsCanvasOpen = (appId: string) => useEmbeddedAppStore((state) => state.openCanvases.has(appId));

export const useDynamicCanvasConfig = (appId: string) =>
    useEmbeddedAppStore((state) => state.dynamicCanvases.get(appId));
